import React, { useMemo } from "react";
import "./ModelVersionsComparison.scss";

function formatVersion(version) {
  if (version == null || version === "") return "—";
  const str = String(version);
  return str.startsWith("v") || str.startsWith("V") ? str : `v${str}`;
}

function formatValue(value) {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length ? value.map(String).join(", ") : "—";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function getMetricSummary(model) {
  const metrics = Array.isArray(model?.metrics) ? model.metrics : [];
  const metricObj = metrics[0];
  if (!metricObj) return "—";
  const value =
    metricObj["Metric Value(%)"] ??
    metricObj.metric ??
    metricObj.accuracy ??
    metricObj.jaccard_index ??
    metricObj.dice_coefficient ??
    null;
  if (value == null) return "—";
  return String(value).includes("%") ? String(value) : `${value}%`;
}

function buildRows(models) {
  const fields = [
    {
      id: "name",
      label: "Name",
      getValue: (m) => m.name,
    },
    {
      id: "framework",
      label: "Framework",
      getValue: (m) => m.framework,
    },
    {
      id: "task",
      label: "Task",
      getValue: (m) => m.task_type,
    },
    {
      id: "architecture",
      label: "Architecture",
      getValue: (m) => m.model_architecture,
    },
    {
      id: "domain",
      label: "Domain",
      getValue: (m) => m.model_domain,
    },
    {
      id: "dataset",
      label: "Benchmark Dataset",
      getValue: (m) => m.benchmark_dataset,
    },
    {
      id: "classes",
      label: "Classes",
      getValue: (m) => m?.config?.exp_config?.data?.class_names,
    },
    {
      id: "backbone",
      label: "Feature Extractor",
      getValue: (m) =>
        m?.config?.exp_config?.model?.["Feature Extractor"] ||
        m?.config?.exp_config?.model?.feature_extractor,
    },
    {
      id: "formats",
      label: "Formats",
      getValue: (m) => m.model_graph_format,
    },
    {
      id: "metric",
      label: "Primary Metric",
      getValue: getMetricSummary,
      tone: "metric",
    },
    {
      id: "platform",
      label: "Platform Trained",
      getValue: (m) => m.platform_trained,
    },
    {
      id: "ownership",
      label: "Ownership",
      getValue: (m) => m.ownership,
    },
    {
      id: "organization",
      label: "Organization",
      getValue: (m) => m.organization,
    },
    {
      id: "created",
      label: "Created On",
      getValue: (m) => m.created_on,
    },
  ];

  return fields.map((field) => {
    const values = models.map((model) => formatValue(field.getValue(model)));
    const hasDiff = values.some((value) => value !== values[0]);
    return { ...field, values, hasDiff };
  });
}

export default function ModelVersionsComparison({
  models = [],
  onChangeSelection,
}) {
  const rows = useMemo(() => buildRows(models), [models]);

  if (!models.length) {
    return (
      <div className="text-secondary py-4 text-center">
        Select at least 2 versions to compare.
      </div>
    );
  }

  return (
    <div className="model-versions-comparison">
      <div className="model-versions-comparison__header d-a justify-content-between gap-2 flex-wrap mb-3">
        <div>
          <h6 className="mb-1 fw-bold">Version Comparison</h6>
          <p className="mb-0 text-secondary small-font">
            Comparing {models.length} versions side by side. Differing rows are highlighted.
          </p>
        </div>
        {onChangeSelection ? (
          <button
            type="button"
            className="btn button-secondary btn-sm px-3"
            onClick={onChangeSelection}
          >
            Change selection
          </button>
        ) : null}
      </div>

      <div className="model-versions-comparison__table-wrap">
        <table className="model-versions-comparison__table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              {models.map((model) => (
                <th key={model._id} scope="col" title={model.name}>
                  {formatVersion(model.version)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={row.hasDiff ? "is-diff" : undefined}
              >
                <td>{row.label}</td>
                {row.values.map((value, index) => (
                  <td
                    key={`${row.id}-${models[index]?._id || index}`}
                    className={row.tone === "metric" ? "is-metric" : undefined}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
